import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, MessageCircle } from "lucide-react";

/**
 * "Contacter Renforteo" button + modal, dropped into the candidate dashboard.
 * Sends the message to the admin via POST /api/candidates/contact-admin.
 */
export default function ContactAdminButton() {
    const [open, setOpen] = useState(false);
    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");
    const [sending, setSending] = useState(false);

    const submit = async () => {
        if (message.trim().length < 3) {
            toast.error("Message trop court.");
            return;
        }
        setSending(true);
        try {
            await api.post("/candidates/contact-admin", { subject, message });
            toast.success("Message envoyé à l'équipe Renforteo.");
            setOpen(false);
            setSubject("");
            setMessage("");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur d'envoi.");
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            <button
                onClick={() => setOpen(true)}
                className="btn-ghost-navy text-sm inline-flex items-center gap-2"
                data-testid="candidate-contact-admin-btn"
            >
                <MessageCircle size={16} /> Contacter Renforteo
            </button>
            {open && (
                <div
                    className="fixed inset-0 z-50 bg-[#0B192C]/70 backdrop-blur-sm flex items-center justify-center p-4"
                    onClick={() => setOpen(false)}
                    data-testid="contact-admin-modal"
                >
                    <div
                        className="bg-white rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex justify-between items-start p-6 border-b border-slate-100">
                            <div>
                                <div className="overline text-[#F5A623]">Support</div>
                                <h2 className="font-display text-xl mt-1">Écrivez à l'équipe</h2>
                                <p className="text-xs text-slate-500 mt-1">
                                    Question, disponibilité, incident — nous vous répondons rapidement.
                                </p>
                            </div>
                            <button
                                onClick={() => setOpen(false)}
                                className="text-slate-400 hover:text-slate-600"
                                data-testid="contact-admin-close"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="text-sm font-semibold block mb-2">
                                    Sujet <span className="text-xs text-slate-400 font-normal">(facultatif)</span>
                                </label>
                                <input
                                    type="text"
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                    maxLength={150}
                                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-[#F5A623] focus:ring-1 focus:ring-[#F5A623]/40 outline-none"
                                    placeholder="Ex. : Question sur ma mission de vendredi"
                                    data-testid="contact-admin-subject"
                                />
                            </div>
                            <div>
                                <label className="text-sm font-semibold block mb-2">Message</label>
                                <textarea
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    rows={5}
                                    maxLength={5000}
                                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-[#F5A623] focus:ring-1 focus:ring-[#F5A623]/40 outline-none"
                                    placeholder="Bonjour, ..."
                                    data-testid="contact-admin-message"
                                />
                                <div className="text-[10px] text-slate-400 text-right mt-1">
                                    {message.length}/5000
                                </div>
                            </div>
                        </div>
                        <div className="p-4 border-t border-slate-100 flex justify-end gap-2 bg-slate-50">
                            <button
                                onClick={() => setOpen(false)}
                                className="btn-ghost-navy text-sm"
                                data-testid="contact-admin-cancel"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={submit}
                                disabled={sending || message.trim().length < 3}
                                className="btn-gold text-sm disabled:opacity-50"
                                data-testid="contact-admin-submit"
                            >
                                {sending ? "Envoi…" : "Envoyer"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
